import { AfterViewInit, Component, EventEmitter, Inject, Input, Output } from '@angular/core';

import { Observable, combineLatest } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { MandatoryMap } from '../../../../shared/helpers/mandatory-map.class';
import { SubjectRelay } from '../../../../shared/commands/subject-relay.class';

import { OuvrageTLR } from '../../../structure/ouvrage/entities/ouvrage-tlr.entity';

import { TransfertResponsabiliteService } from '../services/transfert-responsabilite.service';
import { TransfertResponsabiliteTLR } from '../entities/transfert-responsabilite-tlr.entity';
import { Parc } from '../entities/simplified-transfert.entity';

import { LocalDateTime } from 'src/main/angular/app/shared/jsr310-localdates/types/local-date-time.type';

import { UserService } from '../../user/services/user.service';
import { UserTLR } from '../../user/entities/user-tlr.entity';

import { Events, EventBusService } from '../../../../technical-modules/events/services/event-bus.service';

class MetaTransfert {
	transfert!: TransfertResponsabiliteTLR;
	class!: string;
	date!: LocalDateTime;
	emetteur!: UserTLR;
	destinataire!: UserTLR;
	antennesCompletes!: string;
	parcsComplets!: string;
	parcsIncomplets!: Parc[];
	fermable!: boolean;
	annulable!: boolean;
	acceptable!: boolean;
	refusable!: boolean;
}

@Component({
	selector: 'transferts-cac-en-cours',
	templateUrl: 'transferts-cac-en-cours.html',
	styleUrls: ['transferts-cac-en-cours.css'],
})
export class TransfertsCacEnCoursComponent implements AfterViewInit {
	etatSuffixes = ['', 'en cours', 'en cours', 'en cours', 'annulé', 'refusé', 'terminé'];
	etatLabels = ['' , 'Initialisé 1/2', 'Accepté 1/2', 'Confirmé 2/2', 'Annulé 2/2', 'Refusé 2/2', 'Terminé'];
	
	user!: UserTLR;
	sortedMetasTransferts: MetaTransfert[] = [];
	
	getLabelOuvrages = (ouvrages: OuvrageTLR[]) => ouvrages.map($ => $.nom).join(", ");
	
	constructor(
		@Inject(TransfertResponsabiliteService) private transfertResponsabiliteService: TransfertResponsabiliteService,
		@Inject(UserService) userService: UserService,
		@Inject(EventBusService) private eventBusService: EventBusService,
	) {
		// Charger l'utilisateur courant
		let user$ = userService.getCurrentUserTLR();
		user$.subscribe(
			(user) => this.user = user
		)
		
		// Charger tous les utilisateurs par id
		let usersById$ = userService.getUserTLRMapById().pipe(
			map(
				(data) => new MandatoryMap(data, "User")
			)
		);

		let transferts$ = this.resfreshDataCommands.pipe(
			mergeMap(
				() => this.transfertResponsabiliteService.transfertsEnCours()
			)
		);
		
		let metasTranferts$ = combineLatest([transferts$, user$, usersById$]).pipe(
			map(
				([transferts, user, usersById]) => {
					return transferts.map(
						(simplifiedTransfert) => {
							let transfert = simplifiedTransfert.data;
							
							let classes: string[] = [];
							if (this.transfertMasque(transfert, user)) {
								classes.push('transfertMasque');
							}
							
							if (this.transfertRefused(transfert, user)) {
								classes.push('panel-red');
							}
							
							if (this.transfertAssigned(transfert, user) || this.emittedTransfertStillAssigned(transfert, user)) {
								classes.push('panel-orange');
							}
							
							if (this.transfertEnded(transfert, user) && !this.transfertRefused(transfert, user)) {
								classes.push('panel-green');
							}

							let te: MetaTransfert = {
								transfert: transfert,
								class: classes.join(", "),
								date: transfert.dateTransfert,
								emetteur: usersById.get(transfert.emetteurId),
								destinataire: usersById.get(transfert.destinataireId),
								antennesCompletes: simplifiedTransfert.antennesCompletes.join(", "),
								parcsComplets: simplifiedTransfert.parcsComplets.join(", "),
								parcsIncomplets: simplifiedTransfert.parcsIncomplets,
								fermable: this.transfertFermable(transfert, user),
								annulable: this.transfertAnnulable(transfert, user),
								acceptable: this.transfertAcceptable(transfert, user),
								refusable: this.transfertRefusable(transfert, user),
							}
							
							return te;
						}
					)
				}
			)
		);
		
		metasTranferts$.subscribe(
			(metastransferts) => {
				this.sortedMetasTransferts = metastransferts;
			}
		)
	}

	ngAfterViewInit() {
		this.resfreshDataCommands.next();
	}
	
	private resfreshDataCommands = new SubjectRelay();
	@Input() set refreshCommands(obs: Observable<void> | null) {
		this.resfreshDataCommands.registerSource(obs);
	}
	
	@Output() userCredentialChanged = new EventEmitter<void>();
	
	@Output() stateTransitionNeeded = new EventEmitter<void>();
  	
  	private transfertMasque(transfert: TransfertResponsabiliteTLR, user: UserTLR) {
  		if (transfert.etat == 9) {
  			// le transfert est a l'etat 9, les deux l'ont vu
  			return true;
  		}
  		if (transfert.etat == 7 && transfert.emetteurId == user.id) {
  			// le transfert est a l'etat 7, l'emetteur l'a vu
  			return true;
  		} else if (transfert.etat == 8 && transfert.destinataireId == user.id) {
  			// le transfert est a l'etat 8, le destinataire l'a vu
  			return true;
  		}
  		// dans tous les autres cas, on ne peut le fermer
  		return false;
  	}

  	private transfertRefused(transfert: TransfertResponsabiliteTLR, _user: UserTLR) {
  		return (transfert.etat == 5);
  	}
  	
  	private transfertAssigned(transfert: TransfertResponsabiliteTLR, user: UserTLR) {
  		return (transfert.destinataireId == user.id && transfert.etat < 4);
  	};

  	private emittedTransfertStillAssigned(transfert: TransfertResponsabiliteTLR, user: UserTLR) {
  		return (transfert.emetteurId == user.id && transfert.etat < 4);
  	};

  	private transfertEnded(transfert: TransfertResponsabiliteTLR, _user: UserTLR) {
  		return (transfert.etat >= 4);
  	};

	/////////////////////////////////////
	// Gestion des actions utilisateur //
	/////////////////////////////////////
	  	
  	// le transfert concerne-t-il l'utilisateur?
  	private concerneUtilisateur(transfert: TransfertResponsabiliteTLR, user: UserTLR) {
  		if (transfert.emetteurId === user.id) {
  			// l'emetteur est l'utilisateur connecte 
  			if (transfert.etat == 2) {
  				return true;
  			}
  		} else if (transfert.destinataireId === user.id) {
  			// le destinataire est l'utilisateur 
  			if (transfert.etat == 1 || transfert.etat == 3 ) {
  				return true;
  			}
  		}
  		
  		return false;
  	}
  	
  	private transfertFermable(transfert: TransfertResponsabiliteTLR, _user: UserTLR) {
  		return transfert.etat >= 4;
  	};
  
    fermerTransfert(transfert: TransfertResponsabiliteTLR) {
		this.transfertResponsabiliteService.fermer(transfert.id, this.user.uid).subscribe(
			(_result) => {
				this.stateTransitionNeeded.emit();
				this.resfreshDataCommands.next();

				// On rafraichit les données de l'utilisateur pour
    			// prendre en compte le transfert de responsabilités
				this.userCredentialChanged.emit();
    		}
		);  
    };
  	
  	private transfertAnnulable(transfert: TransfertResponsabiliteTLR, user: UserTLR) {
  		return !this.concerneUtilisateur(transfert, user) && transfert.etat < 4;
  	};
    
    annulerTransfert(transfert: TransfertResponsabiliteTLR) {
		this.transfertResponsabiliteService.annulerTransfert(transfert.id).subscribe(
			() => {
				this.stateTransitionNeeded.emit();
				this.resfreshDataCommands.next();
			}
		);  
    };     
  	
  	private transfertAcceptable(transfert: TransfertResponsabiliteTLR, user: UserTLR) {
  		return this.concerneUtilisateur(transfert, user) && transfert.etat < 4;
  	};

    accepterTransfert(transfert: TransfertResponsabiliteTLR) {
		this.transfertResponsabiliteService.accepterTransfert(transfert.id, this.user.uid).subscribe(
			(_result) => {
				this.stateTransitionNeeded.emit();
				this.resfreshDataCommands.next();
				// On rafraichit les données de l'utilisateur pour
				// prendre en compte le transfert de responsabilités
				this.userCredentialChanged.emit();
				// emettre un signal pour le refresh du menu
				this.eventBusService.emit(Events.RefreshUserMenuRequested, null);
			}
		);  
    };
  	
  	private transfertRefusable(transfert: TransfertResponsabiliteTLR, user: UserTLR) {
  		return this.concerneUtilisateur(transfert, user) && transfert.etat <= 3;
  	};
    
    refuserTransfert(transfert: TransfertResponsabiliteTLR) {
    	this.transfertResponsabiliteService.refusStep2(transfert.id, this.user.uid).subscribe(
			(_result) => {
				this.stateTransitionNeeded.emit();
				this.resfreshDataCommands.next();
			}
 		);
    };
}